import { useEffect, useRef, useState } from "react";

import type {
  DownloadBanner,
  GetLanguageLabel,
  MarketingBannerRow,
  MarketingBannersTranslations,
  RenameBannerHandlers,
} from "../types";

import { BottomSheet } from "../../BottomSheet";
import { Button as ButtonV2 } from "../../Button";
import {
  CheckIcon,
  MinimizeIcon as CollapseIcon,
  CopyIcon,
  DownloadIcon,
  EditIcon,
  ExpandIcon,
} from "../../Icons";
import { Typo } from "../../Typo";
import { BannerBottomSheetRename } from "../BannerBottomSheetRename";
import { BannerInfoList } from "../BannerInfoList";
import { BannerPreviewSkeleton } from "../BannerPreviewSkeleton";
import { CreativePreview } from "../CreativePreview";
import { RenameBannerFooter } from "../RenameBannerFooter";

const COPIED_RESET_MS = 3000;

const COLLAPSED_PREVIEW_CONTAINER_MIN_HEIGHT = 144;
const COLLAPSED_PREVIEW_MAX_WIDTH = 220;
const COLLAPSED_PREVIEW_MAX_HEIGHT = 96;

type BannerPreviewMobileProps = {
  isOpen: boolean;
  onClose: () => void;
  banner: MarketingBannerRow;
  translations: MarketingBannersTranslations;
  getLanguageLabel: GetLanguageLabel;
  isLoading?: boolean;
  onDownload?: DownloadBanner;
  renameBanner?: RenameBannerHandlers;
  showLastClicked?: boolean;
  showCampaign?: boolean;
  /** Fired before the "Copy HTML code" button writes to the clipboard. */
  onCopy?: () => void;
};

type View = "preview" | "rename";

export const BannerPreviewMobile = ({
  isOpen,
  onClose,
  banner,
  translations,
  getLanguageLabel,
  isLoading = false,
  onDownload,
  renameBanner,
  showLastClicked = false,
  showCampaign = false,
  onCopy,
}: BannerPreviewMobileProps) => {
  const [view, setView] = useState<View>("preview");
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    };
  }, []);

  // Reset view/expanded/copied when the sheet closes, without a set-state-in-
  // effect: adjust state directly while rendering the isOpen transition (see
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes).
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) {
      setView("preview");
      setIsExpanded(false);
      setCopied(false);
    }
  }

  // The timer cancellation itself is a real side effect (not a state
  // adjustment), so it stays in an effect rather than the render-time block above.
  useEffect(() => {
    if (!isOpen && copiedTimeoutRef.current) {
      clearTimeout(copiedTimeoutRef.current);
    }
  }, [isOpen]);

  const { copied: copiedLabel } = translations.actions;
  const { copyLabel } = translations.actions.getCopyLabels(banner.type);

  const canDownload = banner.type === "image_banner" && !!onDownload;

  const handleClose = () => {
    setView("preview");
    setIsExpanded(false);
    onClose();
  };

  const handleCopy = async () => {
    onCopy?.();
    try {
      await navigator.clipboard.writeText(banner.creative_code);
      setCopied(true);
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(
        () => setCopied(false),
        COPIED_RESET_MS,
      );
    } catch (err) {
      console.error("BannerPreviewMobile: failed to copy creative_code", err);
    }
  };

  const handleDownload = async () => {
    try {
      await onDownload?.(banner);
    } catch (err) {
      console.error("BannerPreviewMobile: failed to download banner", err);
    }
  };

  const handleRenameStart = () => {
    if (!renameBanner) return;
    renameBanner.startRename(banner);
    setView("rename");
  };

  const renameFooter = renameBanner ? (
    <RenameBannerFooter
      isSaving={renameBanner.isSaving}
      isSaveDisabled={renameBanner.isSaveDisabled}
      translations={translations}
      onCancel={() => setView("preview")}
      onSave={async () => {
        const success = await renameBanner.save();
        if (success) setView("preview");
      }}
    />
  ) : undefined;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      isDraggable
      contentContainerClassName="p-[unset]"
      footer={view === "rename" ? renameFooter : undefined}
    >
      {view === "preview" &&
        (isLoading ? (
          <BannerPreviewSkeleton />
        ) : (
          <div
            data-testid="banner-preview-modal"
            className="flex w-full flex-col"
          >
            <div className="flex items-center justify-between gap-4 px-4 pb-2">
              <Typo
                type="headingMD"
                className="min-w-0 flex-1 truncate"
                textColor="text-text-primary"
              >
                {banner.name}
              </Typo>
              {renameBanner && (
                <button
                  type="button"
                  onClick={handleRenameStart}
                  className="flex shrink-0 items-center gap-1 text-text-secondary"
                >
                  <EditIcon width={12} height={12} />
                  <Typo type="labelXS" textColor="text-text-secondary">
                    {translations.actions.rename}
                  </Typo>
                </button>
              )}
            </div>

            <div className="flex w-full flex-col gap-4 p-4">
              <div className="relative w-full overflow-hidden rounded-lg bg-surface-secondary">
                <div
                  data-testid="banner-preview-scroll-area"
                  className={isExpanded ? "w-full overflow-x-auto" : "w-full"}
                >
                  <div
                    className="flex w-fit min-w-full items-center justify-center p-4"
                    style={{
                      minHeight: isExpanded
                        ? undefined
                        : COLLAPSED_PREVIEW_CONTAINER_MIN_HEIGHT,
                    }}
                  >
                    {isExpanded ? (
                      <CreativePreview banner={banner} frameless />
                    ) : (
                      <CreativePreview
                        banner={banner}
                        maxWidth={COLLAPSED_PREVIEW_MAX_WIDTH}
                        maxHeight={COLLAPSED_PREVIEW_MAX_HEIGHT}
                        frameless
                      />
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsExpanded((prev) => !prev)}
                  aria-label={
                    isExpanded
                      ? translations.preview.collapse
                      : translations.preview.expand
                  }
                  className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-lg bg-surface-inverse-primary text-text-inverse-primary"
                >
                  {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                </button>
              </div>

              <div className="flex items-center gap-2">
                <ButtonV2
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={handleCopy}
                  data-testid="banner-preview-copy-button"
                >
                  <div className="flex items-center justify-center gap-1">
                    {copied ? (
                      <span className="text-text-positive-default">
                        <CheckIcon width={16} height={16} />
                      </span>
                    ) : (
                      <CopyIcon width={16} height={16} />
                    )}
                    <Typo type="labelXS">
                      {copied ? copiedLabel : copyLabel}
                    </Typo>
                  </div>
                </ButtonV2>
                {canDownload && (
                  <ButtonV2
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={handleDownload}
                    data-testid="banner-preview-download-button"
                  >
                    <div className="flex items-center justify-center gap-1 text-text-primary">
                      <DownloadIcon
                        width={16}
                        height={16}
                        className="[&_path]:stroke-text-primary"
                      />
                      <Typo type="labelXS">
                        {translations.actions.downloadPng}
                      </Typo>
                    </div>
                  </ButtonV2>
                )}
              </div>
            </div>

            <div className="px-4 pb-4">
              <BannerInfoList
                banner={banner}
                translations={translations}
                getLanguageLabel={getLanguageLabel}
                showLastClicked={showLastClicked}
                showCampaign={showCampaign}
              />
            </div>
          </div>
        ))}

      {view === "rename" && renameBanner && (
        <BannerBottomSheetRename
          name={renameBanner.name}
          error={renameBanner.error}
          translations={translations}
          onNameChange={renameBanner.changeName}
          onBack={() => setView("preview")}
          onClose={handleClose}
        />
      )}
    </BottomSheet>
  );
};
